import BaseApiClient from "../api/baseApiClient.service";
import {
	PasswordChangeRequestFailure,
	type LoginRequest,
	type LoginResponse,
	type PasswordChangeConfirm,
	type PasswordChangeErrorResponse,
	type PasswordChangeRequest,
	type TokenValidityResponse,
} from "@/lib/auth/auth.types";
import {
	type Auth as FirebaseAuth,
	getAuth,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signOut,
	updateProfile,
	verifyBeforeUpdateEmail,
} from "firebase/auth";
import { toast } from "sonner";

type AuthResponse = {
	token: string;
};

/**
 * Authentication client
 * - uses firebase for authentication
 * - provides information about the user
 */
class AuthService {
	private static instance: AuthService;
	public auth: FirebaseAuth;
	// we are using the base api client to authenticate with the backend
	private baseApi: BaseApiClient;

	private constructor() {
		this.auth = getAuth();
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): AuthService {
		if (!AuthService.instance) {
			AuthService.instance = new AuthService();
		}
		return AuthService.instance;
	}

	/**
	 * Receive the firebase token to use it to authenticate against the taxpeer backend
	 * Sets the token in the cookie.
	 *
	 * @param data - The login request data containing email and password
	 * @returns The login response from the backend
	 * @throws Error if the email or password is missing or if the login fails
	 */
	public async login(data: LoginRequest): Promise<LoginResponse> {
		try {
			if (!data.email || !data.password) {
				return Promise.reject("Email and password are required");
			}

			// First, authenticate with Firebase
			const userCredential = await signInWithEmailAndPassword(
				this.auth,
				data.email,
				data.password,
			);

			// Get the Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			// Use the token to authenticate with our backend
			const response = await this.upgradeFirebaseIdToJWT(firebaseToken);

			return Promise.resolve(response);
		} catch (error: unknown) {
			if (error instanceof Error) {
				toast.error("Bitte überprüfe deine Anmeldeinformationen");
				return Promise.reject(error.message);
			}
			return Promise.reject("Fehler beim Login");
		}
	}

	/**
	 * Send a verification email to the user
	 */
	public async sendEmailVerification(): Promise<void> {
		const currentUser = this.auth.currentUser;
		if (!currentUser) {
			throw new Error("No user is currently signed in");
		}

		await sendEmailVerification(currentUser);
		toast.success("Bestätigungsemail wurde erfolgreich an dich gesendet.");
	}

	/**
	 * Forgot password
	 *
	 * Sends a password reset email to the user
	 * @param email - The email of the user to send the password reset email to
	 * @throws Error if the email is missing or if the password reset email fails to send
	 */
	public async forgotPassword(email: string): Promise<void> {
		const auth = getAuth();
		sendPasswordResetEmail(auth, email)
			.then(() => {
				toast.success(
					`Passwort-Reset-Email wurde erfolgreich an ${email} gesendet. Prüfe deine E-Mails und dein Spam-Ordner.`,
				);
			})
			.catch((_: Error) => {
				toast.error("Fehler beim Senden des Passwort-Reset-Emails");
			});
	}

	/**
	 * Authenticate with the backend using Firebase token and receive a JWT token in return
	 *
	 * This will present the firebase token to the backend, receive a token in return on success
	 * @param firebaseToken - The firebase token to authenticate with the backend
	 * @returns The auth response from the backend
	 * @throws Error if the authentication fails
	 */
	public async upgradeFirebaseIdToJWT(
		firebaseToken: string,
	): Promise<AuthResponse> {
		try {
			const response = await this.baseApi.post<
				AuthResponse,
				{ firebaseToken: string }
			>("auth/login", { firebaseToken });
			return response;
		} catch (error: unknown) {
			toast.error("Fehler beim Authentifizieren");
			// Normal error handling for other cases
			if (error instanceof Error) {
				return Promise.reject(error.message);
			}
			return Promise.reject("Fehler beim Authentifizieren");
		}
	}

	/**
	 * Logout the current user
	 */
	public async logout(): Promise<void> {
		try {
			await signOut(this.auth);
		} catch (error: unknown) {
			if (error instanceof Error) {
				throw new Error(error.message);
			}
		}
	}

	/**
	 * This function updates the user's display name via firebase
	 * @param newDisplayName
	 */
	public async updateUserDisplayName(newDisplayName: string): Promise<boolean> {
		try {
			const currentUser = this.auth.currentUser;

			if (!currentUser) {
				throw new Error("No user is currently signed in");
			}

			await updateProfile(currentUser, { displayName: newDisplayName });
			return Promise.resolve(true);
		} catch (error: unknown) {
			if (error instanceof Error) {
				toast.error("Fehler beim Aktualisieren des Anzeigenamen");
				return Promise.resolve(false);
			}
			return Promise.reject("Fehler beim Aktualisieren des Anzeigenamen");
		}
	}

	/**
	 * Updates the current user's email address
	 *
	 * @param newEmail - The new email address
	 * @returns A promise that resolves when the verification email has been sent
	 * @throws Error if the email update process fails
	 */
	public async updateUserEmail(newEmail: string): Promise<boolean> {
		try {
			const currentUser = this.auth.currentUser;

			if (!currentUser) {
				throw new Error("No user is currently signed in");
			}

			// Configure the action code settings with redirect URL
			const actionCodeSettings = {
				url: `${window.location.origin}/`,
				handleCodeInApp: true,
			};

			// Send verification email to the new address before updating with redirect URL
			await verifyBeforeUpdateEmail(currentUser, newEmail, actionCodeSettings);

			toast.success(
				`Bestätigungsmail wurde an ${newEmail} gesendet. Bitte überprüfe deine E-Mails und bestätige die Änderung.`,
			);

			return Promise.resolve(true);
		} catch (error: unknown) {
			if (error instanceof Error) {
				// Handle specific error cases
				if (error.message.includes("auth/requires-recent-login")) {
					toast.error(
						"Für diese Aktion ist eine erneute Anmeldung erforderlich. Bitte melde dich ab und wieder an.",
					);
				} else {
					toast.error("Fehler beim Aktualisieren der Email-Adresse");
					return Promise.resolve(false);
				}
			}
			return Promise.reject("Fehler beim Aktualisieren der Email-Adresse");
		}
	}

	/**
	 * Login with a custom authentication token
	 *
	 * @param customToken - The custom authentication token
	 * @returns The login response from the backend
	 * @throws Error if the login with custom token fails
	 */
	public async loginWithCustomToken(
		customToken: string,
	): Promise<LoginResponse> {
		try {
			// Sign in with the custom token
			const userCredential = await signInWithCustomToken(
				this.auth,
				customToken,
			);

			console.log("Signing in with custom token", userCredential);

			// Get the Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			console.log("Firebase token from custom token", firebaseToken);

			// Use the token to authenticate with our backend
			const response = await this.upgradeFirebaseIdToJWT(firebaseToken);

			return Promise.resolve(response);
		} catch (error: unknown) {
			if (error instanceof Error) {
				toast.error("Fehler bei der Anmeldung mit Token");
				return Promise.reject(error.message);
			}
			return Promise.reject("Fehler bei der Anmeldung mit Token");
		}
	}

	// Change password functions

	/**
	 * Request a password change by sending an email with a token
	 *
	 * @param data - The password change request data containing email
	 * @returns A promise that resolves when the password change request is sent
	 * @throws Error if the email is missing or if the request fails
	 */
	public async passwordChangeRequest(
		data: PasswordChangeRequest,
	): Promise<void> {
		try {
			if (!data.email) {
				throw new Error("Email ist erforderlich");
			}

			await this.baseApi.post<void, PasswordChangeRequest>(
				"password-change/password-change-request",
				data,
			);

			toast.success(
				"Wenn dieser Account existiert, wurde eine Passwort-Reset-Email an dich gesendet.",
			);
		} catch (error: unknown) {
			// Check if the error is an axios error with response data
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: PasswordChangeErrorResponse };
				};
				const errorData = axiosError.response?.data;

				if (errorData?.errorCode) {
					switch (errorData.errorCode) {
						case PasswordChangeRequestFailure.LIMIT_REACHED:
							toast.error(
								"Zu viele Passwort-Reset-Anfragen. Bitte versuche es später erneut.",
							);
							break;
						case PasswordChangeRequestFailure.UNKNOWN_ERROR:
							toast.error(
								"Wenn dieser Account existiert, wurde eine Passwort-Reset-Email an dich gesendet.",
							);
							break;
						default:
							toast.error(
								"Ein unbekannter Fehler ist aufgetreten. Bitte versuche es später erneut.",
							);
							break;
					}
					return Promise.reject(errorData.error);
				}
			}
		}
	}

	/**
	 * Confirm a password change using a token and new password
	 *
	 * @param data - The password change confirmation data containing token and new password
	 * @returns A promise that resolves when the password change is confirmed
	 * @throws Error if the token or password is missing or if the confirmation fails
	 */
	public async passwordChangeConfirm(
		data: PasswordChangeConfirm,
	): Promise<void> {
		try {
			if (!data.token || !data.password) {
				throw new Error("Token und Passwort sind erforderlich");
			}

			await this.baseApi.post<void, PasswordChangeConfirm>(
				"password-change/password-change-confirm",
				data,
			);

			toast.success(
				"Passwort erfolgreich geändert. Du kannst dich jetzt mit deinem neuen Passwort anmelden.",
			);
		} catch (error: unknown) {
			// Check if the error is an axios error with response data
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: PasswordChangeErrorResponse };
				};
				const errorData = axiosError.response?.data;

				if (errorData?.errorCode) {
					switch (errorData.errorCode) {
						case PasswordChangeRequestFailure.USER_NOT_FOUND:
							toast.error(
								"Benutzer nicht gefunden. Bitte fordere eine neue Passwort-Reset an.",
							);
							break;
						case PasswordChangeRequestFailure.LIMIT_REACHED:
							toast.error(
								"Zu viele Passwort-Reset-Anfragen. Bitte versuche es später erneut.",
							);
							break;
						default:
							toast.error(
								"Ein unbekannter Fehler ist aufgetreten. Bitte versuche es später erneut.",
							);
							break;
					}
					throw new Error(errorData.error);
				}
			}

			// Fallback for other error types
			if (error instanceof Error) {
				toast.error("Fehler beim Ändern des Passworts");
				throw new Error(error.message);
			}
			throw new Error("Fehler beim Ändern des Passworts");
		}
	}

	/**
	 * Check if a password change token is valid (as example, when opening the password change page so we can show the user a message if it's invalid)
	 *
	 * @param token - The token to validate
	 * @returns A promise that resolves to the token validity response
	 * @throws Error if the token is missing or if the validation fails
	 */
	public async checkTokenValidity(
		token: string,
	): Promise<TokenValidityResponse> {
		try {
			if (!token) {
				throw new Error("Token ist erforderlich");
			}

			const response = await this.baseApi.get<TokenValidityResponse>(
				`password-change/check-token-validity/${token}`,
			);

			return response;
		} catch (error: unknown) {
			if (error instanceof Error) {
				return {
					isValid: false,
				};
			}
			return {
				isValid: false,
			};
		}
	}
}

export default AuthService;
